/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormErrorComponent } from '@app/common/components/form-error.component';

import { clusterListByManagementTeams, clustersLoaded, currentClusterInfo, user } from '@app/core/ngrx';
import { AppState, ExtractedErrorText } from '@app/core/types';
import { bytesToMb, megabytesToBytes } from '@app/domain/information-units';
import { userFullName } from '@app/domain/user';

import { gmcErrorsBundle } from '@app/common/components/gmc-errors/bundle';
import { DetectEllipsisDirective } from '@app/common/directives/detect-ellipsis.directive';
import { asFilesMaxTotalSizeValidatorResut, validateFilesMaxTotalSize } from '@app/common/validators/file';
import { isTruthy } from '@common/utils';
import { Store } from '@ngrx/store';
import { ClusterId } from '@shared/types/cluster';
import { flow, identity, round } from 'lodash-es';
import { Observable, Subject, combineLatest, filter, map, startWith, switchMap, take, tap } from 'rxjs';
import { MapPipe } from '../../common/pipes/map.pipe';
import { ContactUsEvent, ContactUsService } from './contact-us.service';
import { FileInputComponent } from './file-input/file-input.component';

export const DIALOG_CONTACT_US_ID = 'DIALOG_CONTACT_US_ID';

@Component({
  selector: 'dialog-contact-us',
  templateUrl: './dialog-contact-us.component.html',
  styleUrls: ['./dialog-contact-us.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormErrorComponent,
    FileInputComponent,
    NgClass,
    AsyncPipe,
    gmcErrorsBundle,
    MapPipe,
    DetectEllipsisDirective,
  ],
})
export default class DialogContactUsComponent implements OnDestroy {
  constructor(private store: Store<AppState>) {
    this.store
      .select(user)
      .pipe(take(1), filter(isTruthy))
      .subscribe((user) => {
        this.form.controls.email.setValue(user.email);
        this.form.controls.name.setValue(userFullName(user));
      });

    this.store
      .select(clustersLoaded)
      .pipe(
        filter(isTruthy),
        switchMap(() => this.store.select(currentClusterInfo)),
        take(1),
        filter(isTruthy),
      )
      .subscribe(({ id }) => this.form.controls.clusterId.setValue(id));
  }
  private destroyRef = inject(DestroyRef);

  clusterListByManagementTeams$ = this.store.select(clusterListByManagementTeams);
  readonly messageMaxLength = 50_000;
  public maxFilesTotalSizeInBytes = megabytesToBytes(100);
  public maxFileSizeInBytes = megabytesToBytes(100);
  public asFilesMaxTotalSizeValidatorResut = asFilesMaxTotalSizeValidatorResut;
  public formatBytes = flow(bytesToMb, round);
  private service = inject(ContactUsService);
  private requestEvents$ = new Subject<ContactUsEvent | null>();
  public uploadProgressInPercents$: Observable<number | null> = this.requestEvents$.pipe(
    map((e) => {
      if (e?.type === 'progress') {
        const result = round((e.loaded / e.total) * 100);
        return Number.isNaN(result) ? 0 : result;
      } else {
        return null;
      }
    }),
  );
  public isUploading$ = this.uploadProgressInPercents$.pipe(
    startWith(null),
    map((progress) => progress !== null),
  );
  public serverError$: Observable<ExtractedErrorText | null> = this.requestEvents$.pipe(
    map((e) => (e?.type === 'error' ? e.message : null)),
  );

  form = new FormGroup({
    email: new FormControl<string>('', [Validators.email, Validators.required]),
    clusterId: new FormControl<ClusterId | null>(null),
    name: new FormControl<string>('', [Validators.maxLength(200)]),
    subject: new FormControl<string>('', [Validators.maxLength(200)]),
    message: new FormControl<string>('', [Validators.required, Validators.maxLength(this.messageMaxLength)]),
    files: new FormArray<FormControl<File | null>>([], [validateFilesMaxTotalSize(this.maxFilesTotalSizeInBytes)]),
  });

  public isSubmitButtonDisabled$: Observable<boolean> = combineLatest([
    this.form.valueChanges.pipe(
      map(() => this.form.invalid),
      startWith(this.form.invalid),
    ),
    this.isUploading$,
  ]).pipe(map(([isFormInvalid, isUploading]) => isFormInvalid || isUploading));

  onSubmit() {
    const { name, email, clusterId, subject, message, files } = this.form.getRawValue();
    if (this.form.invalid || email === null || message === null) return;
    const payload = { name: name ?? null, email, clusterId, subject: subject || null, message };
    this.requestEvents$.next(null); // Reset the errors and progress
    this.service
      .sendSupportRequest(payload, files.filter(isTruthy))
      .pipe(
        tap((e) => this.requestEvents$.next(e)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.requestEvents$.complete();
  }

  // Silences the NG0956 warning (https://angular.dev/errors/NG0956)
  public identity = identity;
}
